import { atom, selector } from "recoil";
import Constants from "expo-constants";
import { Mission, TimeSheet, Cct, EarningType } from "../__generated__/graphql";
import { Buildable } from "ts-essentials";
import { IjmSelection, MissionWithIjm } from "../utils";
import * as Sentry from "@sentry/react-native";

export const allMissionsState = atom<Mission[] | undefined>({
    key: "allMissions",
    default: undefined,
});

export const allTimesheetsState = atom<TimeSheet[] | undefined>({
    key: "allTimesheets",
    default: undefined,
});

type CctLoadContext = {
    stateCode: string;
    locale: string;
};

export const cctLoadContextState = atom<CctLoadContext>({
    key: "CctLoadContext",
    default: {
        stateCode: "",
        locale: "",
    },
});

export const newMissionState = atom({
    key: "newMission",
    default: {} as Buildable<MissionWithIjm>,
});

/**
 * This section is for CCT management recoil atoms and selector
 * The `allCCTState` atom should never be set manually, you must use the Hook useLoadCCTs ( cct-utils.ts )
 * All selectors usage based on allCCTState must call the useLoadCCTs hook first
 *
 * TODO: All this CCT management should be re-written to use the Apollo cache instead of recoil
 * Note that the allCct list is based on a contract address state, we should probably use a currentContract atom as source
 * element and use selectors to derives the allCCT list from it
 * Event a ContextManager could be used to manage the current contract and allCCT list
 */
export const allCCTState = atom<Cct[] | undefined>({
    key: "allCCTs",
    default: undefined,
});

export const defaultCCTState = selector<Cct | null>({
    key: "defaultCCT",
    get: ({ get }) => {
        const allCCT = get(allCCTState);
        const matchingCCT = allCCT?.filter((cct) => {
            return cct.cctContractId === Constants.expoConfig?.extra?.cctDefaultContractId;
        });
        if (matchingCCT && matchingCCT.length > 0) {
            return matchingCCT[0];
        } else {
            return null;
        }
    },
});

export const liftBasedNoCCTState = selector<Cct | null>({
    key: "liftNoCTT",
    get: ({ get }) => {
        const allCCT = get(allCCTState);
        const matchingCCT = allCCT?.filter((cct) => {
            return cct.cctContractId.startsWith("lift-no-cct");
        });
        if (matchingCCT && matchingCCT.length > 0) {
            if (matchingCCT.length > 1) {
                Sentry.captureMessage("More than 1 no CCT found in the list of CCTs", "warning");
            }
            return matchingCCT[0];
        } else {
            return null;
        }
    },
});

export const editIjmSelection = atom({
    key: "editIjmSelection",
    default: {} as Buildable<IjmSelection>,
});

type editIjmSelectionCallbackType = {
    func?: (action: "save" | "cancel") => void | Promise<void>;
};

export const editIjmSelectionCallback = atom<editIjmSelectionCallbackType>({
    key: "editIjmSelectionCallback",
    default: { func: undefined },
});

export type EditIjmSalaryInfo = {
    amount: number;
    earningType: EarningType;
};

export const editIjmSalaryInfo = atom({
    key: "editIjmSalaryInfo",
    default: undefined as EditIjmSalaryInfo | undefined,
});
